<script setup lang="ts">
/**
 * 代理商的授權申請表單（建立草稿／編輯草稿共用）——批93-C §2.2。
 *
 * 為什麼建立與編輯是同一支：草稿的意義就是「可以反覆改到對為止」，兩份表單會立刻分岔
 * （其中一邊漏帶配額之類）。差別只有「哪些欄位鎖住」——送出後 `customer`／`product`／
 * `kind`／指紋都不可再動（它們決定這張證綁在誰的哪台機器上）。
 *
 * ★★ 表單最重要的一件事是**把核發政策講在按下去之前**：
 *   - 選的套裝政策允許代理商免審核發、而且內容**逐字等於模板** → 按鈕是「確認並核發」，
 *     按下去當場就簽出一張證。
 *   - 只要改了模組／配額／期限任何一項 → 政策強制升為 superadmin，按鈕變成
 *     「確認送出（待審核）」並即時顯示「自訂內容需超級管理者核發」（批97 起文案全中文，
 *     來源＝`lib/roles.ts::ROLE_LABELS`）。
 *   判定是 `lib/activation.ts` 的前端鏡像，**伺服器才是權威**（confirm 回應會帶回真正的
 *   policy，不一致時以回應為準顯示）。
 */
import { computed, ref, watch } from 'vue'

import ModulePicker from '@/components/ModulePicker.vue'
import QuotaEditor from '@/components/QuotaEditor.vue'
import TermField from '@/components/TermField.vue'
import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import type { LicenseReq, ProductSpec } from '@/api/ls'
import * as reseller from '@/api/reseller'
import type { ResellerCustomer, ResellerLicense } from '@/api/reseller'
import { canActivateDirectly, confirmButtonLabel, policyFor, policyHint } from '@/lib/activation'
import { activationPolicyLabel, defaultTermTotal, issuablePlans, termLabel, termOptionsWith } from '@/lib/products'
import { quotaSummaryText } from '@/lib/quotas'
import { seedQuotas } from '@/lib/seedQuotas'
import { seedRequestDraft } from '@/lib/requestDraftSeed'
import { splitMinutes, totalMinutes } from '@/lib/terms'
import { customerOptionLabel } from '@/lib/customerLabel'
import { useDialogsStore } from '@/stores/dialogs'
import { NO_FILE_DROPPED_MESSAGE, useFileDrop, useWindowFileDrop } from '@/lib/fileDrop'

const props = defineProps<{
  open: boolean
  /** 非 null＝編輯既有草稿（客戶／產品／類型鎖住）。 */
  draft: LicenseReq | null
  customers: ResellerCustomer[]
  products: ProductSpec[]
  licenses: ResellerLicense[]
}>()
const emit = defineEmits<{ close: []; saved: [] }>()

const dialogs = useDialogsStore()
const busy = ref(false)

type Kind = 'new' | 'change' | 'renew' | 'addon'
const KIND_LABELS: Record<Kind, string> = {
  new: '新授權',
  change: '變更內容',
  renew: '續約',
  addon: '加購模組',
}
/** 下拉用（不要在模板裡 `v-for` 物件——鍵會退化成 string，`v-model` 的型別就散了）。 */
const KIND_OPTIONS: { key: Kind; label: string }[] = (
  ['new', 'change', 'renew', 'addon'] as Kind[]
).map((k) => ({ key: k, label: KIND_LABELS[k] }))
const KIND_HINTS: Record<Kind, string> = {
  new: '客戶主機第一次安裝：請附上該台跑 install.sh 產生的 request.json（含機器指紋）。',
  change: '既有主機改內容（加配額／換套裝／開模組）——指紋與產品沿用現行授權，不重新綁機。',
  renew: '既有授權到期續約——內容沿用，只換到期日；順便換套裝也可以。',
  addon: '在主證之外單獨加一個模組／一段配額，獨立到期、可單獨撤銷（不動主證）。',
}

const customerId = ref('')
const productKey = ref('')
const kind = ref<Kind>('new')
const baseLicenseId = ref('')
const note = ref('')

// 內容
const plan = ref('')
/** 期限的**總分鐘**（批108）；`null`＝沿用套裝／現行證。送出前用 `splitMinutes` 拆兩欄。 */
const termTotal = ref<number | null>(null)
const modules = ref<Record<string, string>>({})
/**
 * 數量配額（批114 起是**唯一**的數量入口；獨立的數字欄已拔除）。
 * `quotasValid` 是 `QuotaEditor` 依憑證設計的必填旗標回報的——起手 true（訊號還沒到
 * 之前不該把送出鈕反灰）。
 */
const quotas = ref<Record<string, number | null> | null>(null)
const quotasValid = ref(true)
/** ModulePicker／QuotaEditor 的 initFrom 只在掛載時跑 → 帶入值或**換產品**都要 bump。 */
const pickerKey = ref(0)

// request.json（只有 kind='new' 用得到）
const reqText = ref('')
const reqFileName = ref('')
const fileInput = ref<HTMLInputElement | null>(null)

const spec = computed<ProductSpec | null>(
  () => props.products.find((p) => p.key === productKey.value) ?? null,
)
const plans = computed(() => issuablePlans(spec.value))
// 選項要含「目前選到的天數」——套裝可以有產品清單裡沒有的期限（試用 90 天）。
const termOptions = computed(() => termOptionsWith(spec.value, termTotal.value))
/** 沒有任何來源（套裝／現行證）可帶時的期限預設（`defaultTermTotal`：產品預設 → 清單第一個）。 */
const termFallback = computed(() => defaultTermTotal(spec.value))
/** 這位客戶名下、可以當作變更／續約／加購基準的授權。 */
const baseCandidates = computed(() =>
  props.licenses.filter((l) => l.customer_id === customerId.value && l.status === 'active'),
)

const isEdit = computed(() => !!props.draft)

/**
 * 目前表單內容（政策判定與送出都吃這一份）。
 *
 * ★ 批114：**數量一律只住 `quotas`**（獨立的數字欄已拔除——同一個數字兩個輸入框，
 *   而且獨立欄會靜默蓋掉配額表填的值）。政策判定那一側
 *   （`lib/activation.ts::contentMatchesPlan`）已改成「一級鍵缺席時看 `quotas` 同名鍵」。
 */
const content = computed(() => ({
  modules: modules.value,
  quotas: quotas.value,
  term_days: termTotal.value == null ? null : splitMinutes(termTotal.value)[0],
  term_minutes: termTotal.value == null ? null : splitMinutes(termTotal.value)[1],
}))
// 批102 V7：`kind` 要一起傳——`keep_expiry` 的預設判準依它而定（`change`→true、
// 其餘→false）。目前這張表單不收那三個新欄，傳了也只是讓判準與後端逐字對齊。
const policy = computed(() => policyFor(spec.value, plan.value || null, content.value, kind.value))
const canDirect = computed(() => canActivateDirectly('reseller', policy.value.policy))
const hint = computed(() => policyHint(policy.value, canDirect.value))

/**
 * 基準授權下拉上的配額摘要（批114 裁示 ⑰）——前兩個鍵＋`+N`，標籤查該證**自己產品**的
 * 憑證設計（下拉裡可能同時有兩個產品的授權）。沒有宣告配額時回空字串，整段省略。
 */
function baseSummary(l: ResellerLicense): string {
  const s = props.products.find((p) => p.key === l.product) ?? null
  const text = quotaSummaryText(s, seedQuotas(s, l))
  return text ? `${text}・` : ''
}

/** 套裝 chip 上的小標：這個方案能不能由代理商自己免審核發。 */
function planPolicyTag(policyValue: string): string {
  return policyValue === 'reseller' ? '此方案可免審核發' : '需我方審核'
}

function resetContent() {
  plan.value = ''
  termTotal.value = termFallback.value
  modules.value = {}
  quotas.value = null
  pickerKey.value++
}

/** 選套裝＝整包帶入模板（之後手動改任何一項，政策就會升為 superadmin，畫面會即時說）。 */
function applyPlan(key: string) {
  plan.value = key
  const p = plans.value.find((x) => x.key === key)
  if (!p) return
  modules.value = { ...p.modules }
  // 批114：模板的相容數字欄併進配額包（只讀 `quotas` 會讓那些數字在套用模板時消失）。
  quotas.value = seedQuotas(spec.value, p)
  // ★ 批108 truthy 陷阱：`term_days` 是 0（分鐘級套裝）時也要帶入。
  if (p.term_days != null || p.term_minutes) {
    termTotal.value = totalMinutes(p.term_days, p.term_minutes)
  }
  pickerKey.value++
}

/** 換產品＝整包重置（不同產品的模組鍵／配額鍵／套裝完全不通用）。 */
function changeProduct(key: string) {
  productKey.value = key
  resetContent()
}

/** 換基準授權：把它現在的內容帶進來當起點（續約／變更的常態）。 */
function applyBase(licenseId: string) {
  baseLicenseId.value = licenseId
  const lic = baseCandidates.value.find((l) => l.license_id === licenseId)
  if (!lic) return
  if (lic.product) productKey.value = lic.product
  // 加購是「在主證之外另外加」，起手一律空白——沿用主證內容會讓人以為要重填一次整張證。
  if (kind.value === 'addon') {
    resetContent()
    return
  }
  plan.value = lic.plan ?? ''
  modules.value = { ...(lic.modules ?? {}) }
  // 批114：舊證的相容數字欄併進配額包（批114 之前簽的證數量住在一級欄位上）。
  quotas.value = seedQuotas(spec.value, lic)
  const licTotal =
    lic.term_days != null || lic.term_minutes
      ? totalMinutes(lic.term_days, lic.term_minutes)
      : null
  termTotal.value =
    licTotal != null && termOptions.value.includes(licTotal)
      ? licTotal
      : termFallback.value
  pickerKey.value++
}

// --- request.json 解析（前端就地顯示確認，別讓人送出一份自己沒看過的檔案）---
const parsed = computed<Record<string, unknown> | null>(() => {
  const raw = reqText.value.trim()
  if (!raw) return null
  try {
    const obj = JSON.parse(raw)
    return obj && typeof obj === 'object' && !Array.isArray(obj)
      ? (obj as Record<string, unknown>)
      : null
  } catch {
    return null
  }
})
const badJson = computed(() => !!reqText.value.trim() && !parsed.value)
const parsedInstance = computed(() => String(parsed.value?.instance_id ?? '') || '—')
const parsedFingerprint = computed(() => {
  const fp = parsed.value?.machine_fingerprint
  return fp && typeof fp === 'object' ? Object.keys(fp as object).join('、') : '—'
})
const parsedProduct = computed(() => String(parsed.value?.product ?? ''))
watch(parsedProduct, (v) => {
  // 檔案自己寫了產品就照它選（改成別的會被後端 422 擋下，不如一開始就對齊）。
  if (v && props.products.some((p) => p.key === v)) changeProduct(v)
})

async function readFile(f: File) {
  reqFileName.value = f.name
  reqText.value = await f.text()
}
function onFile(e: Event) {
  const f = (e.target as HTMLInputElement).files?.[0]
  if (f) void readFile(f)
}
// 拖放邏輯抽在 `lib/fileDrop.ts`（攔 dragenter、抓不到檔案時明確提示）——理由見該檔開頭。
const { over: reqDragOver, handlers: reqDropHandlers } = useFileDrop({
  onFile: readFile,
  onNoFile: () => void dialogs.notify({ title: '沒有抓到檔案', message: NO_FILE_DROPPED_MESSAGE }),
})
// 表單開著時整個視窗都接檔案拖放（不必精準拖進虛線框；放錯位置也不會變成瀏覽器直接開那個檔）。
useWindowFileDrop(() => props.open, reqDropHandlers)

// --- 開窗時初始化 -----------------------------------------------------------
watch(
  () => props.open,
  (o) => {
    if (!o) return
    const d = props.draft
    if (d) {
      customerId.value = d.customer_id
      productKey.value = d.product
      kind.value = (d.kind as Kind) ?? 'new'
      baseLicenseId.value = String(d.request_json?.base_license_id ?? '')
      note.value = d.note ?? ''
      const reqd = (d.request_json?.requested ?? {}) as Record<string, unknown>
      plan.value = d.plan ?? ''
      termTotal.value =
        d.term_days != null || d.term_minutes != null
          ? totalMinutes(d.term_days, d.term_minutes)
          : reqd.term_days != null || reqd.term_minutes != null
            ? totalMinutes(reqd.term_days as number, reqd.term_minutes as number)
            : null
      modules.value = { ...(d.modules_requested ?? (reqd.modules as Record<string, string>) ?? {}) }
      // 批114：草稿的起手配額走與我方後台同一支取值鏈——舊草稿只有相容數字欄時要併進來，
      // 沒有配額包時退回套裝模板（`new`／`change`）或基準授權，否則編輯器會以
      // `absent_default`（多半是 0）起手，一儲存就把全 0 寫回草稿（#5539 的同一個根因）。
      quotas.value = seedRequestDraft({
        kind: kind.value,
        spec: spec.value,
        request: d,
        raw: reqd,
        plan: plans.value.find((p) => p.key === plan.value) ?? null,
        current: baseCandidates.value.find((l) => l.license_id === baseLicenseId.value) ?? null,
      }).quotas
      reqText.value = ''
      reqFileName.value = ''
    } else {
      customerId.value = props.customers[0]?.id ?? ''
      productKey.value = props.products.find((p) => p.status === 'active')?.key ?? props.products[0]?.key ?? ''
      kind.value = 'new'
      baseLicenseId.value = ''
      note.value = ''
      reqText.value = ''
      reqFileName.value = ''
      resetContent()
    }
    pickerKey.value++
  },
  { immediate: true },
)

// --- 送出 -------------------------------------------------------------------

const needsRequestJson = computed(() => !isEdit.value && kind.value === 'new')
const needsBase = computed(() => !isEdit.value && kind.value !== 'new')
const ready = computed(() => {
  if (busy.value) return false
  if (!customerId.value || !productKey.value) return false
  if (needsRequestJson.value && !parsed.value) return false
  if (needsBase.value && !baseLicenseId.value) return false
  // 批114：必填配額（憑證設計宣告的）沒填就先擋——後端核發會回 422 `quotas_invalid`。
  if (!quotasValid.value) return false
  return true
})

function errText(e: unknown, fallback: string): string {
  const d = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  if (d == null) return fallback
  if (typeof d === 'string') return d
  const obj = d as { code?: string; message?: string }
  if (obj.message) return obj.message
  const CODES: Record<string, string> = {
    product_out_of_scope: '這個產品不在貴公司的銷售範圍內，請聯絡我方。',
    pending_exists: '這台主機已經有一筆待審核的申請，請先處理原申請。',
    hosted_instance: '這台是我方代管的站台，授權由中台派送，不走申請流程。',
    not_owner: '這筆草稿不屬於貴公司，無法修改。',
    trial_already_used: '這台主機已經領過試用了。',
  }
  if (obj.code) return CODES[obj.code] ?? obj.code
  return JSON.stringify(d)
}

/** 建立或更新草稿；回傳草稿 id（失敗回 null，錯誤已提示）。 */
async function persist(): Promise<string | null> {
  const body = {
    plan: plan.value || null,
    modules: modules.value,
    // 批114：數量只送這一包（含值為 `null` 的「無上限」）。
    quotas: quotas.value,
    term_days: content.value.term_days,
    term_minutes: content.value.term_minutes,
    note: note.value.trim() || null,
  }
  try {
    if (props.draft) {
      await reseller.updateRequest(props.draft.id, body)
      return props.draft.id
    }
    const created = await reseller.createRequest({
      customer_id: customerId.value,
      product: productKey.value,
      kind: kind.value,
      request_json: kind.value === 'new' ? parsed.value : null,
      base_license_id: kind.value === 'new' ? null : baseLicenseId.value || null,
      ...body,
    })
    return created.id
  } catch (e) {
    await dialogs.notify({ title: '儲存失敗', message: errText(e, '儲存草稿失敗') })
    return null
  }
}

async function saveDraft() {
  if (!ready.value) return
  busy.value = true
  try {
    const id = await persist()
    if (!id) return
    emit('saved')
    emit('close')
  } finally {
    busy.value = false
  }
}

async function saveAndConfirm() {
  if (!ready.value) return
  const ok = await dialogs.confirm({
    title: canDirect.value ? '確認並核發' : '確認送出',
    message: canDirect.value
      ? '這份內容依套裝政策可免審核發——按下確認會「當場簽發」一張授權，之後只能請我方撤銷。要繼續嗎？'
      : '確認送出後內容就鎖定了，不能再修改（要改請等我方退回草稿）。要繼續嗎？',
    confirmText: canDirect.value ? '確認並核發' : '確認送出',
  })
  if (!ok) return
  busy.value = true
  try {
    const id = await persist()
    if (!id) return
    const res = await reseller.confirmRequest(id)
    emit('saved')
    emit('close')
    if (res.status === 'approved') {
      await dialogs.notify({
        title: '已核發',
        message: `授權已簽發（${(res.issued_license_id ?? '').slice(0, 8)}）——請到「授權」頁下載授權檔交給客戶。`,
      })
    } else {
      await dialogs.notify({
        title: '已送出',
        message:
          res.policy_reason === 'custom_content'
            ? '內容與套裝模板不同，已轉為待我方審核。核發後會出現在〈授權〉頁。'
            : '已送出，等待我方審核。核發後會出現在〈授權〉頁。',
      })
    }
  } catch (e) {
    await dialogs.notify({ title: '送出失敗', message: errText(e, '送出失敗') })
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <UiModal
    :open="open"
    width="760px"
    :title="isEdit ? '修改授權草稿' : '建立授權草稿'"
    @close="emit('close')"
  >
    <!-- ① 對象與類型：送出後就鎖住的三件事 -->
    <div class="form">
      <label class="field">
        <span>客戶</span>
        <select v-model="customerId" :disabled="isEdit">
          <option v-for="c in customers" :key="c.id" :value="c.id">{{ customerOptionLabel(c, { ownership: false }) }}</option>
        </select>
      </label>
      <label class="field">
        <span>產品</span>
        <select
          :value="productKey"
          :disabled="isEdit || kind !== 'new'"
          @change="changeProduct(($event.target as HTMLSelectElement).value)"
        >
          <option v-for="p in products" :key="p.key" :value="p.key">{{ p.name }}</option>
        </select>
      </label>
      <label class="field">
        <span>類型</span>
        <select v-model="kind" :disabled="isEdit">
          <option v-for="o in KIND_OPTIONS" :key="o.key" :value="o.key">{{ o.label }}</option>
        </select>
      </label>
    </div>
    <p class="subtle">{{ KIND_HINTS[kind] }}</p>
    <p v-if="!customers.length" class="warn-text">
      ⚠ 還沒有任何客戶——請先到〈我的客戶〉建立一位。
    </p>
    <p v-if="!products.length" class="warn-text">
      ⚠ 貴公司目前沒有可銷售的產品，請聯絡我方確認銷售範圍。
    </p>

    <!-- ② 基準授權（變更／續約／加購）-->
    <label v-if="kind !== 'new'" class="field base-field">
      <span>基準授權</span>
      <select :value="baseLicenseId" :disabled="isEdit" @change="applyBase(($event.target as HTMLSelectElement).value)">
        <option value="">請選擇要{{ KIND_LABELS[kind] }}的授權…</option>
        <!-- 批114 裁示 ⑰：配額摘要用憑證設計的標籤組字（沒有宣告配額的產品整段省略）。 -->
        <option v-for="l in baseCandidates" :key="l.license_id" :value="l.license_id">
          {{ l.instance_name || l.host_instance_id?.slice(0, 12) || l.license_id.slice(0, 8) }}
          ・{{ baseSummary(l) }}到期 {{ l.expires_at ? l.expires_at.slice(0, 10) : '未啟用' }}
        </option>
      </select>
      <small v-if="!baseCandidates.length" class="subtle">這位客戶目前沒有生效中的授權。</small>
    </label>

    <!-- ③ request.json（只有新授權要）-->
    <template v-if="kind === 'new' && !isEdit">
      <h4 class="sub-title">主機識別（request.json）</h4>
      <p class="subtle">
        客戶在主機上跑 <code class="mono">install.sh</code> 後會產生
        <code class="mono">license/request.json</code>——裡面是那台機器的指紋，授權會綁在它上面。
      </p>
      <div
        class="drop"
        :class="{ over: reqDragOver }"
        v-on="reqDropHandlers"
        @click="fileInput?.click()"
      >
        <p>把 <strong>request.json</strong> 拖到這個表單的任何位置，或<strong>點擊選擇檔案</strong></p>
        <input ref="fileInput" type="file" accept=".json,application/json" hidden @change="onFile" />
      </div>
      <label class="field txt-field">
        <span>或直接貼上內容</span>
        <textarea v-model="reqText" rows="4" placeholder='{"product": "...", "instance_id": "...", "machine_fingerprint": {...}}' />
      </label>
      <p v-if="badJson" class="warn-text">⚠ 這段內容不是合法的 JSON——請確認整個檔案都貼進來了。</p>
      <div v-if="parsed" class="ro">
        <div class="ro-row"><span class="ro-k">檔案</span><span class="ro-v">{{ reqFileName || '（貼上的內容）' }}</span></div>
        <div class="ro-row"><span class="ro-k">主機 ID</span><span class="ro-v"><code class="mono">{{ parsedInstance }}</code></span></div>
        <div class="ro-row"><span class="ro-k">指紋</span><span class="ro-v">{{ parsedFingerprint }}</span></div>
        <div v-if="parsedProduct" class="ro-row">
          <span class="ro-k">產品</span><span class="ro-v"><code class="mono">{{ parsedProduct }}</code></span>
        </div>
      </div>
    </template>

    <!-- ④ 內容 -->
    <h4 class="sub-title">授權內容</h4>
    <div class="plan-row">
      <span class="plan-label">套裝</span>
      <button
        v-for="p in plans"
        :key="p.key"
        type="button"
        class="plan-chip"
        :class="{ on: plan === p.key }"
        :title="planPolicyTag(p.activation_policy)"
        @click="applyPlan(p.key)"
      >
        {{ p.label }}
        <small class="pol">{{ planPolicyTag(p.activation_policy) }}</small>
      </button>
      <button type="button" class="plan-chip" :class="{ on: plan === '' }" @click="plan = ''">
        客製
      </button>
    </div>
    <!-- 批114：獨立的數量欄已拔除（數量一律在下方「數量配額」填）。 -->
    <div class="form">
      <label class="field">
        <span>期限</span>
        <!-- 批108：選項與自由輸入都是**總分鐘**（能表達「30 分鐘」「2 小時」）。 -->
        <select v-model.number="termTotal">
          <option v-for="m in termOptions" :key="m" :value="m">{{ termLabel(m) }}</option>
        </select>
        <TermField v-model="termTotal" nullable />
      </label>
    </div>
    <div class="mp">
      <ModulePicker :key="`${productKey}-m-${pickerKey}`" v-model="modules" :spec="spec" />
    </div>
    <h4 class="quota-title">數量配額</h4>
    <div class="mp">
      <!-- 批151：加購申請的配額是子證的值——加總的鍵要填增加量（只多提示，送出不變）。 -->
      <QuotaEditor
        :key="`${productKey}-q-${pickerKey}`"
        v-model="quotas"
        :spec="spec"
        :addon-context="kind === 'addon'"
        @update:valid="quotasValid = $event"
      />
    </div>
    <label class="field txt-field">
      <span>備註（我方會看到）</span>
      <input v-model="note" maxlength="500" placeholder="例：客戶 9/9 來電要求加開行事曆模組" />
    </label>

    <!-- ⑤ 政策提示：按下去之前就要知道會發生什麼事 -->
    <p class="policy" :class="{ ok: canDirect }">
      <strong>{{ activationPolicyLabel(policy.policy) }}</strong>
      <span v-if="hint"> — {{ hint }}</span>
    </p>

    <template #foot>
      <UiButton @click="emit('close')">取消</UiButton>
      <UiButton variant="secondary" :disabled="!ready" :loading="busy" @click="saveDraft">
        儲存草稿
      </UiButton>
      <UiButton variant="primary" :disabled="!ready" :loading="busy" @click="saveAndConfirm">
        {{ confirmButtonLabel(canDirect) }}
      </UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
.form {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-3);
  margin-bottom: var(--sp-2);
}
.form .field {
  min-width: 150px;
  flex: 1;
}
.field span {
  font-size: var(--fs-12);
  color: var(--fg-2);
}
.base-field {
  display: grid;
  gap: 4px;
  margin: var(--sp-3) 0;
}
.sub-title {
  margin: var(--sp-4) 0 var(--sp-1);
  font-size: var(--fs-13);
  color: var(--fg-3);
  font-weight: 600;
}
.quota-title {
  margin: var(--sp-3) 0 var(--sp-1);
  font-size: var(--fs-13);
  color: var(--fg-3);
  font-weight: 600;
}
.drop {
  border: 1px dashed var(--border-strong);
  border-radius: var(--r-md);
  padding: var(--sp-4);
  text-align: center;
  color: var(--fg-3);
  font-size: var(--fs-13);
  cursor: pointer;
  transition: all var(--dur-fast) var(--ease);
}
.drop:hover,
.drop.over {
  border-color: var(--brand-400);
  background: var(--brand-50);
}
.drop p {
  margin: 0;
}
.txt-field {
  display: grid;
  gap: 4px;
  margin-top: var(--sp-3);
}
.txt-field textarea {
  font-family: var(--font-mono, monospace);
  font-size: var(--fs-12);
  padding: 8px 10px;
  border: 1px solid var(--border-strong);
  border-radius: var(--r-sm);
  background: var(--bg-panel);
  color: var(--fg);
  resize: vertical;
}
.ro {
  display: grid;
  gap: 4px;
  padding: var(--sp-3);
  margin-top: var(--sp-2);
  border: 1px solid var(--border);
  border-radius: var(--r-md);
  background: var(--bg);
}
.ro-row {
  display: flex;
  gap: var(--sp-3);
  font-size: var(--fs-13);
}
.ro-k {
  color: var(--fg-3);
  min-width: 72px;
  flex: none;
}
.ro-v {
  color: var(--fg);
  word-break: break-all;
}
.plan-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--sp-2);
  margin-bottom: var(--sp-3);
}
.plan-label {
  font-size: var(--fs-13);
  color: var(--fg-3);
}
.plan-chip {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 4px 12px;
  border: 1px solid var(--border-2);
  border-radius: 999px;
  background: transparent;
  color: var(--fg-2);
  font-size: var(--fs-13);
  cursor: pointer;
}
.plan-chip.on {
  border-color: var(--brand-500);
  background: var(--brand-500);
  color: #fff;
}
.plan-chip .pol {
  font-size: var(--fs-10, 10px);
  opacity: 0.75;
}
.mp {
  margin-bottom: var(--sp-3);
}
/* 政策提示：可免審核發＝中性（不是好消息也不是壞消息，是「按下去會發生什麼」）。 */
.policy {
  margin: var(--sp-3) 0 0;
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--border-strong);
  border-radius: var(--r-sm);
  background: var(--bg);
  color: var(--fg-2);
  font-size: var(--fs-12);
  line-height: var(--lh-base);
}
.policy.ok {
  border-color: var(--brand-400);
  background: var(--brand-50);
  color: var(--brand-700, var(--fg));
}
/* ★ 不可叫 `.warn`（會套到 UiButton variant="warn" 的根節點）。 */
.warn-text {
  margin: var(--sp-2) 0 0;
  color: var(--warning);
  font-size: var(--fs-12);
  line-height: var(--lh-base);
}
/* 手機（批171）：客戶／產品／類型三個下拉一列一個（兩個並排時客戶名與產品名都被切掉）。 */
@media (--mobile) {
  .form .field {
    flex-basis: 100%;
  }
}
</style>
